import { useEffect } from 'react';
import { apiUrl, IS_DEMO } from '../../apiBase';
import { gpuNow, type GpuNow } from './gpuTally';
import { isStandalone } from '../../standalone';

// HOW MUCH EACH SCREEN ASKS OF THE DEVICE, as it is actually met (Louis 2026-10-26): the peak of
// what its 3D views hold, and whether the device took any of them back. A screen of the published
// tutorial, or a phase of the app's own page; read in Phase 7, one row per screen, to see which
// page a phone cannot hold. `route` is where it goes, '' for nowhere yet.
//
// SENT AGAIN EACH TIME THE PEAK GROWS, not once on leaving: a page the phone kills says nothing
// on its way out, and that is exactly the visit worth having. The server keeps one row per visit
// and screen, so the last figures sent are the ones kept.
//
// One visit is one page load: a reload is a new one, as it is a new page for the phone.
const newId = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
const visit = newId();
// WHICH BROWSER AND WHICH PASS, so Phase 7 shows one device's one pass in order rather than an
// average of everybody's. The browser's id is kept in it for good; the pass's in the tab, which
// is what survives the reload a kill causes, so the pages before and after a kill are one pass.
// On an iPhone the installed app and Safari keep separate storage: one phone, two browsers here,
// which is right, since they do not behave alike.
const kept = (store: () => Storage, key: string) => {
  try {
    const was = store().getItem(key);
    if (was) return was;
    const id = newId();
    store().setItem(key, id);
    return id;
  } catch {
    return '';
  }
};
const device = kept(() => localStorage, 'notice.memory.device');
const run = kept(() => sessionStorage, 'notice.memory.run');
const standalone = isStandalone();

const post = (route: string, body: object) => {
  void fetch(apiUrl(route), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // The body's own, where it names them: a kill is said for the dead page's device and pass.
    body: JSON.stringify({ device, run, standalone, ...body }),
  }).catch(() => {});
};

// A PAGE THE DEVICE KILLED, found by the page that replaced it. The open screen is noted in the
// tab (sessionStorage, which outlives the page) and the note is wiped when the page is left the
// ordinary way, `pagehide`. A note still there when a page opens is a page that never got to
// wipe it: killed, on that screen, and restarted by the browser in its place (Louis 2026-10-26).
// It is said for the dead page's own visit and screen, so it lands on its row.
type Open = { route: string; visit: string; screen: string; ord: number; device: string; run: string; standalone: boolean };
const OPEN = 'notice.memory.open';
let open: Open | null = null;
const note = () => {
  try {
    if (open) sessionStorage.setItem(OPEN, JSON.stringify(open));
    else sessionStorage.removeItem(OPEN);
  } catch { /* storage refused: no note, and no report of a kill */ }
};
if (!IS_DEMO) {
  try {
    const dead = JSON.parse(sessionStorage.getItem(OPEN) || 'null') as Open | null;
    sessionStorage.removeItem(OPEN);
    if (dead?.route && dead.visit !== visit) {
      post(dead.route, {
        visit: dead.visit, screen: dead.screen, ord: dead.ord, killed: 1,
        ...(dead.device ? { device: dead.device, run: dead.run, standalone: dead.standalone } : {}),
      });
    }
  } catch { /* nothing readable: nothing to report */ }
  window.addEventListener('pagehide', () => {
    try { sessionStorage.removeItem(OPEN); } catch { /* see note */ }
  });
  // Back from the browser's memory, the same page: open again on the screen it shows.
  window.addEventListener('pageshow', (e) => { if (e.persisted) note(); });
}

export function useScreenMemory(route: string, screen: string, ord: number) {
  useEffect(() => {
    if (IS_DEMO || !route || !screen) return;
    open = { route, visit, screen, ord, device, run, standalone };
    note();
    // Lost on THIS screen: the count runs for the page's life.
    const lostBefore = gpuNow().lost;
    let best: GpuNow | null = null;
    let sent = '';
    const size = (g: GpuNow) => g.geometry + g.page + g.textures;
    // Sent on arriving and every time it grows, and once more on leaving: a screen passed through
    // in a moment is still a screen that was shown, with whatever it had loaded by then.
    const tick = (leaving = false) => {
      const now = gpuNow();
      if (!best || size(now) > size(best)) best = now;
      const lost = now.lost - lostBefore;
      if (!leaving && size(best) <= 0 && !lost) return;
      const r = (n: number) => Math.round(n * 10) / 10;
      const body = {
        visit, screen, ord, lost, views: best.views,
        geometry: r(best.geometry), page: r(best.page), textures: r(best.textures),
        // What the page holds besides, at the latest (see tallyDecoded).
        kept: r(now.kept), files: r(now.files),
        // And the views' surfaces: made so far, released and not yet destroyed, at the latest.
        created: now.created, awaiting: now.awaiting, awaitingMb: r(now.awaitingMb),
      };
      const key = JSON.stringify(body);
      if (key === sent) return;
      sent = key;
      post(route, body);
    };
    tick();
    const timer = setInterval(tick, 500);
    return () => {
      clearInterval(timer);
      tick(true);
      if (open?.screen === screen) { open = null; note(); }
    };
  }, [route, screen, ord]);
}
